import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Stack,
  Typography,
  Button,
  Box,
  TextField,
  Card,
  TableRow,
  TableBody,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from '@mui/material';
import api from '../../api';
import { toast } from 'react-toastify';
import CircularProgressWithLabel from '../../utils/loader';

const INITIAL_QUERY = {
  employeeId: '',
  employeeName: '',
};

const INITIAL_TOTALS = {
  totalPendingAmount: 0,
  totalCollectedAmount: 0,
  totalSubmittedAmount: 0,
};

const INITIAL_COLLECT_FORM = {
  amountReceived: '',
  receivedType: 'cash',
  remarks: '',
};

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString()}`;

const normalizeResponse = (data) => ({
  employees: Array.isArray(data?.employees) ? data.employees : [],
  totals: {
    totalPendingAmount: Number(data?.totalPendingAmount || 0),
    totalCollectedAmount: Number(data?.totalCollectedAmount || 0),
    totalSubmittedAmount: Number(data?.totalSubmittedAmount || 0),
  },
});

const fetchCollections = async (query, isTodayTab) => {
  const endpoint = isTodayTab
    ? '/api/v1/deliveries/today_employee_to_pays'
    : '/api/v1/deliveries/all_employee_to_pays';

  const response = await api.post(endpoint, {
    query: {
      employeeId: query.employeeId || '',
      employeeName: query.employeeName || '',
    },
  });

  if (response?.data?.success) {
    return normalizeResponse(response.data);
  }

  throw new Error(response?.data?.message || 'Unable to load collections.');
};

export default function Collections() {
  const [activeTab, setActiveTab] = useState(0);
  const [collectDialogOpen, setCollectDialogOpen] = useState(false);
  const [collectSubmitting, setCollectSubmitting] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [collectForm, setCollectForm] = useState(INITIAL_COLLECT_FORM);

  const [todayRows, setTodayRows] = useState([]);
  const [todayTotals, setTodayTotals] = useState(INITIAL_TOTALS);
  const [todayLoading, setTodayLoading] = useState(false);
  const [todayQuery, setTodayQuery] = useState(INITIAL_QUERY);

  const [allRows, setAllRows] = useState([]);
  const [allTotals, setAllTotals] = useState(INITIAL_TOTALS);
  const [allLoading, setAllLoading] = useState(false);
  const [allQuery, setAllQuery] = useState(INITIAL_QUERY);

  const isTodayTab = activeTab === 0;
  const loading = isTodayTab ? todayLoading : allLoading;
  const rows = isTodayTab ? todayRows : allRows;
  const totals = isTodayTab ? todayTotals : allTotals;
  const selectedQuery = useMemo(
    () => (isTodayTab ? todayQuery : allQuery),
    [isTodayTab, todayQuery, allQuery]
  );

  const loadData = async (tab, queryToUse) => {
    const todayTab = tab === 0;

    try {
      if (todayTab) setTodayLoading(true);
      else setAllLoading(true);

      const { employees, totals: nextTotals } = await fetchCollections(queryToUse, todayTab);

      if (todayTab) {
        setTodayRows(employees);
        setTodayTotals(nextTotals);
      } else {
        setAllRows(employees);
        setAllTotals(nextTotals);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Unable to load collections.');
      if (todayTab) {
        setTodayRows([]);
        setTodayTotals(INITIAL_TOTALS);
      } else {
        setAllRows([]);
        setAllTotals(INITIAL_TOTALS);
      }
    } finally {
      if (todayTab) setTodayLoading(false);
      else setAllLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 0) {
      loadData(0, todayQuery);
      return;
    }
    loadData(1, allQuery);
  }, [activeTab]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleQueryChange = (event) => {
    const { name, value } = event.target;
    if (isTodayTab) {
      setTodayQuery((prev) => ({ ...prev, [name]: value }));
      return;
    }
    setAllQuery((prev) => ({ ...prev, [name]: value }));
  };

  const onClickSearch = () => {
    if (isTodayTab) {
      loadData(0, todayQuery);
      return;
    }
    loadData(1, allQuery);
  };

  const onClickReset = () => {
    if (isTodayTab) {
      setTodayQuery(INITIAL_QUERY);
      loadData(0, INITIAL_QUERY);
      return;
    }
    setAllQuery(INITIAL_QUERY);
    loadData(1, INITIAL_QUERY);
  };

  const handleOpenCollectDialog = (employeeRow) => {
    setSelectedEmployee(employeeRow);
    setCollectForm(INITIAL_COLLECT_FORM);
    setCollectDialogOpen(true);
  };

  const handleCloseCollectDialog = () => {
    if (collectSubmitting) return;
    setCollectDialogOpen(false);
    setSelectedEmployee(null);
    setCollectForm(INITIAL_COLLECT_FORM);
  };

  const handleCollectFormChange = (event) => {
    const { name, value } = event.target;
    setCollectForm((prev) => ({ ...prev, [name]: value }));
  };

  const formatReceivedAt = () => {
    const date = new Date();
    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absoluteOffset = Math.abs(offsetMinutes);
    const offsetHours = String(Math.floor(absoluteOffset / 60)).padStart(2, '0');
    const offsetMins = String(absoluteOffset % 60).padStart(2, '0');
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMins}`;
  };

  const onSubmitCollect = async () => {
    if (!selectedEmployee?.employeeId) {
      toast.error('Employee is required.');
      return;
    }

    const amountReceived = Number(collectForm.amountReceived);
    if (!amountReceived || amountReceived <= 0) {
      toast.error('Amount received is required.');
      return;
    }

    const pendingSubmissionAmount = Number(selectedEmployee?.pendingSubmissionAmount || 0);
    if (pendingSubmissionAmount > 0 && amountReceived > pendingSubmissionAmount) {
      toast.error(`Amount exceeds pending to-pay for this employee. Pending: ${formatCurrency(pendingSubmissionAmount)}`);
      return;
    }

    try {
      setCollectSubmitting(true);

      const response = await api.post('/api/v1/deliveries/receive_employee_to_pay', {
        data: {
          employeeId: selectedEmployee.employeeId,
          amountReceived,
          receivedType: collectForm.receivedType,
          remarks: collectForm.remarks || '',
          receivedAt: formatReceivedAt(),
        },
      });

      if (response?.data?.success) {
        toast.success(response?.data?.message || 'Amount collected successfully.');
        handleCloseCollectDialog();
        if (isTodayTab) {
          loadData(0, todayQuery);
        } else {
          loadData(1, allQuery);
        }
        return;
      }

      toast.error(response?.data?.message || 'Unable to collect amount.');
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      const backendPendingAmount = error?.response?.data?.pendingAmount;
      if (backendPendingAmount !== undefined && backendPendingAmount !== null) {
        toast.error(`${backendMessage || 'Amount exceeds pending to-pay for this employee'} (Pending: ${formatCurrency(backendPendingAmount)})`);
      } else {
        toast.error(backendMessage || error?.message || 'Unable to collect amount.');
      }
    } finally {
      setCollectSubmitting(false);
    }
  };

  return (
    <Container maxWidth={false}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" gutterBottom>
          Collections
        </Typography>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Today Collections" />
          <Tab label="All Collections" />
        </Tabs>
      </Card>

      <Card sx={{ mb: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ p: 2 }}
        >
          <TextField
            label="Employee ID"
            name="employeeId"
            size="small"
            fullWidth
            value={selectedQuery.employeeId}
            onChange={handleQueryChange}
          />
          <TextField
            label="Employee Name"
            name="employeeName"
            size="small"
            fullWidth
            value={selectedQuery.employeeName}
            onChange={handleQueryChange}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={onClickSearch}>
              Search
            </Button>
            <Button variant="outlined" onClick={onClickReset}>
              Reset
            </Button>
          </Stack>
        </Stack>
      </Card>

      <Card>
        {loading ? (
          <CircularProgressWithLabel />
        ) : (
          <TableContainer>
            <Table sx={{ minWidth: 1000 }} size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">SNo</TableCell>
                  <TableCell align="center">Employee ID</TableCell>
                  <TableCell align="center">Employee Name</TableCell>
                  <TableCell align="center">Deliveries Count</TableCell>
                  <TableCell align="center">Total To Pay Amount</TableCell>
                  <TableCell align="center">Collected Amount</TableCell>
                  <TableCell align="center">Submitted Amount</TableCell>
                  <TableCell align="center">Pending Submission Amount</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length > 0 &&
                  rows.map((row, index) => (
                    <TableRow hover key={row?.employeeId || `${row?.employeeName}-${index}`}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{row?.employeeId || 'NA'}</TableCell>
                      <TableCell align="center">{row?.employeeName || 'NA'}</TableCell>
                      <TableCell align="center">{row?.deliveriesCount || 0}</TableCell>
                      <TableCell align="center">{formatCurrency(row?.totalToPayAmount)}</TableCell>
                      <TableCell align="center">{formatCurrency(row?.collectedAmount)}</TableCell>
                      <TableCell align="center">{formatCurrency(row?.submittedAmount)}</TableCell>
                      <TableCell align="center">{formatCurrency(row?.pendingSubmissionAmount)}</TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleOpenCollectDialog(row)}
                          disabled={Number(row?.pendingSubmissionAmount || 0) <= 0}
                        >
                          Collect
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ height: 200 }}>
                      <Typography variant="h6" color="text.secondary">
                        No collection records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Card sx={{ mt: 2 }}>
        <Box
          sx={{
            p: 2,
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(3, 1fr)',
            },
          }}
        >
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total Pending Amount
            </Typography>
            <Typography variant="h6">{formatCurrency(totals.totalPendingAmount)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total Collected Amount
            </Typography>
            <Typography variant="h6">{formatCurrency(totals.totalCollectedAmount)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total Submitted Amount
            </Typography>
            <Typography variant="h6">{formatCurrency(totals.totalSubmittedAmount)}</Typography>
          </Box>
        </Box>
      </Card>

      <Dialog open={collectDialogOpen} onClose={handleCloseCollectDialog} fullWidth maxWidth="sm">
        <DialogTitle>Collect To-Pay Amount</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Employee"
              value={selectedEmployee?.employeeName || ''}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Pending Amount"
              value={formatCurrency(selectedEmployee?.pendingSubmissionAmount || 0)}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Amount Received"
              name="amountReceived"
              type="number"
              fullWidth
              size="small"
              value={collectForm.amountReceived}
              onChange={handleCollectFormChange}
            />
            <TextField
              select
              label="Received Type"
              name="receivedType"
              fullWidth
              size="small"
              value={collectForm.receivedType}
              onChange={handleCollectFormChange}
            >
              <MenuItem value="cash">cash</MenuItem>
              <MenuItem value="upi">upi</MenuItem>
              <MenuItem value="bank account">bank account</MenuItem>
            </TextField>
            <TextField
              label="Remarks"
              name="remarks"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={collectForm.remarks}
              onChange={handleCollectFormChange}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCollectDialog} disabled={collectSubmitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onSubmitCollect} disabled={collectSubmitting}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
